import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, ApiError, uploadAttachment, type ChangeDetail, type UatRoundView } from '../lib/api';
import { dateTime, shortDate } from '../lib/format';

// Veta Change · M4: la entrega del cambio en su detalle.
// Aprobado → Desarrollo → Pruebas del usuario (⇄ Corrección) → Paso a producción → Cierre.

const field = 'h-9 w-full rounded-md border border-[#D6D3CD] bg-white px-2.5 text-sm focus:border-accent focus:outline-none disabled:bg-paper';
const area = 'w-full rounded-md border border-[#D6D3CD] bg-white px-2.5 py-2 text-sm focus:border-accent focus:outline-none';
const btnPrimary = 'inline-flex h-9 items-center rounded-md bg-accent px-4 text-sm font-semibold text-white hover:bg-accent-dark disabled:opacity-50';
const btnSecondary = 'inline-flex h-9 items-center rounded-md border border-[#D6D3CD] bg-white px-3 text-sm text-[#3F3D39] hover:bg-paper disabled:opacity-50';

const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

type Props = { d: ChangeDetail; onDone: (d: ChangeDetail, text: string) => void; onError: (e: unknown) => void };

export function ChangeDeliveryPanel({ d, onDone, onError }: Props) {
  const delivery = d.delivery;
  const can = Boolean(d.permissions.deliver);
  const n = d.ticket.number;
  const post = (path: string, body: unknown = {}) => api<ChangeDetail>(`/change/${n}/${path}`, { method: 'POST', body: JSON.stringify(body) });

  return (
    <section className="flex flex-col gap-4 rounded-lg border border-line bg-white p-4 sm:p-5" aria-labelledby="delivery-title">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id="delivery-title" className="text-base font-semibold">Entrega</h2>
          <p className="mt-0.5 text-xs text-muted">Desarrollo, pruebas del usuario, paso a producción y cierre.</p>
        </div>
        {delivery?.commitment && <CommitmentMeter c={delivery.commitment} />}
      </div>

      {d.stage === 'approved' && <StartStep d={d} can={can} run={() => post('start')} onDone={onDone} onError={onError} />}
      {(d.stage === 'development' || d.stage === 'correction') && (
        <SendUatStep d={d} can={can} run={notes => post('uat', { notes })} onDone={onDone} onError={onError} />
      )}
      {d.stage === 'uat' && delivery?.uat && (
        <UatStep d={d} round={delivery.uat} can={can} run={body => post('uat/record', body)} onDone={onDone} onError={onError} />
      )}
      {d.stage === 'deployment' && !delivery?.production && (
        <ProductionStep can={can} run={body => post('production', body)} onDone={onDone} onError={onError} />
      )}
      {d.stage === 'deployment' && delivery?.production && (
        <CloseStep d={d} can={can} run={body => post('close', body)} onDone={onDone} onError={onError} />
      )}
      {d.stage === 'closed' && <ClosedSummary d={d} />}

      {delivery && delivery.uatHistory.length > 0 && (
        <details className="rounded-md border border-[#EFEDE9] px-3 py-2 text-sm">
          <summary className="cursor-pointer text-xs font-semibold text-muted">Rondas anteriores de pruebas ({delivery.uatHistory.length})</summary>
          <div className="mt-2 flex flex-col gap-3">{delivery.uatHistory.map(r => <RoundResult key={r.round} r={r} />)}</div>
        </details>
      )}
    </section>
  );
}

// ---------- fecha compromiso ----------

const HEALTH: Record<string, { tone: string; bar: string; label: (c: NonNullable<NonNullable<ChangeDetail['delivery']>['commitment']>) => string }> = {
  ok: { tone: 'text-accent-dark', bar: 'bg-accent', label: c => (c.dueAt ? `Vence aprox. ${dateTime(c.dueAt)}` : 'En tiempo') },
  warning: { tone: 'text-[#6A3A00]', bar: 'bg-[#D98E04]', label: c => (c.dueAt ? `Por vencer: ${dateTime(c.dueAt)}` : 'Por vencer') },
  late: { tone: 'text-[#8F1D1D]', bar: 'bg-[#C0392B]', label: () => 'Fecha compromiso vencida' },
  paused: { tone: 'text-muted', bar: 'bg-[#B8B3AA]', label: c => (c.pausedReason === 'uat' ? 'En pausa: pruebas del usuario' : 'En pausa: esperando al cliente') },
  met: { tone: 'text-accent-dark', bar: 'bg-accent', label: () => 'Entregado a tiempo' },
  missed: { tone: 'text-[#8F1D1D]', bar: 'bg-[#C0392B]', label: () => 'Entregado fuera de tiempo' }
};

function CommitmentMeter({ c }: { c: NonNullable<NonNullable<ChangeDetail['delivery']>['commitment']> }) {
  const h = HEALTH[c.health] ?? HEALTH.ok!;
  const pct = Math.min(100, Math.round(c.pct * 100));
  return (
    <div className="w-full max-w-[260px] text-xs" aria-label="Fecha compromiso">
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-semibold text-ink">Fecha compromiso · {c.businessDays} días hábiles</span>
        <span className="font-mono tabular-nums text-muted">{Math.round(c.pct * 100)} %</span>
      </div>
      <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-[#EFEDE9]" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <div className={`h-full rounded-full ${h.bar}`} style={{ width: `${pct}%` }} />
      </div>
      <p className={`mt-1 ${h.tone}`}>{h.label(c)}</p>
    </div>
  );
}

// ---------- pasos ----------

function useAction<A>(run: (arg: A) => Promise<ChangeDetail>, onDone: Props['onDone'], onError: Props['onError'], text: string) {
  return useMutation<ChangeDetail, unknown, A>({ mutationFn: run, onSuccess: r => onDone(r, text), onError });
}

function StartStep({ d, can, run, onDone, onError }: { d: ChangeDetail; can: boolean; run: () => Promise<ChangeDetail> } & Omit<Props, 'd'>) {
  const m = useAction(run, onDone, onError, 'Desarrollo iniciado: el ticket quedó en progreso.');
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-md bg-paper px-3 py-3 text-sm">
      <p>El cliente aprobó la cotización. Inicia el desarrollo cuando empiece el trabajo{d.evolutive?.status === 'proposed' ? ' (el evolutivo en Veta sigue pendiente de confirmar)' : ''}.</p>
      {can && <button type="button" className={btnPrimary} disabled={m.isPending} onClick={() => m.mutate(undefined)}>Iniciar desarrollo</button>}
    </div>
  );
}

function SendUatStep({ d, can, run, onDone, onError }: { d: ChangeDetail; can: boolean; run: (notes: string) => Promise<ChangeDetail> } & Omit<Props, 'd'>) {
  const [notes, setNotes] = useState('');
  const m = useAction(run, onDone, onError, 'Enviado a pruebas del usuario: el cliente recibió la lista por correo y en el portal.');
  const last = d.delivery?.uat;
  const onHold = d.ticket.status === 'on_hold';
  const criteria = d.acceptanceCriteria ?? [];
  return (
    <div className="flex flex-col gap-3">
      {d.stage === 'correction' && last && (
        <div className="rounded-md border border-[#F2C9C9] bg-[#FDF3F3] px-3 py-2.5 text-sm">
          <p className="font-semibold text-[#8F1D1D]">En corrección · el cliente rechazó la ronda {last.round}</p>
          <ul className="mt-1.5 space-y-1">
            {last.items.filter(i => i.result === 'rejected').map((i, k) => (
              <li key={k}><span className="font-medium">{i.criterion}</span><span className="block text-xs text-[#5E2A2A]">“{i.comment}”</span></li>
            ))}
          </ul>
          <p className="mt-1.5 text-xs text-muted">Las horas de corrección van dentro de lo cotizado. Si piden algo fuera de alcance, es una nueva estimación.</p>
        </div>
      )}
      <div>
        <p className="text-sm font-medium">El cliente validará estos criterios de aceptación</p>
        <ol className="mt-1.5 list-decimal space-y-0.5 pl-5 text-sm text-[#3F3D39]">{criteria.map((c, i) => <li key={i}>{c}</li>)}</ol>
      </div>
      {can && (
        <>
          <label className="flex flex-col gap-1 text-sm font-medium"><span>Nota para el cliente <span className="font-normal text-muted">(opcional: ambiente, datos de prueba, cómo probar)</span></span>
            <textarea rows={2} className={area} value={notes} onChange={e => setNotes(e.target.value)} />
          </label>
          {onHold && <p className="text-xs text-[#6A3A00]">El ticket está en espera: reanúdalo para enviar a pruebas.</p>}
          <div>
            <button type="button" className={btnPrimary} disabled={m.isPending || onHold || !criteria.length} onClick={() => m.mutate(notes)}>
              {d.stage === 'correction' ? `Reenviar a pruebas (ronda ${(last?.round ?? 0) + 1})` : 'Enviar a pruebas del usuario'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

const RESULT_CHIP = {
  pending: 'bg-[#EDEBE7] text-muted',
  accepted: 'bg-accent-soft text-accent-dark',
  rejected: 'bg-[#FBEAEA] text-[#8F1D1D]'
} as const;
const RESULT_LABEL = { pending: 'Pendiente', accepted: 'Aceptado', rejected: 'Rechazado' } as const;

function RoundResult({ r }: { r: UatRoundView }) {
  return (
    <div className="text-sm">
      <p className="text-xs text-muted">
        Ronda {r.round} · enviada {shortDate(r.sentAt)}
        {r.outcome && ` · ${r.outcome === 'accepted' ? 'aceptada' : 'rechazada'}${r.via === 'auto' ? ' por vencimiento' : r.decidedBy ? ` por ${r.decidedBy.replace(/\s*\(.*\)$/, '')}` : ''} ${dateTime(r.decidedAt)}`}
        {r.via === 'agent' && r.evidence && ` · evidencia: ${r.evidence}`}
      </p>
      <ul className="mt-1.5 flex flex-col gap-1.5">
        {r.items.map((i, k) => (
          <li key={k} className="flex items-start gap-2">
            <span className={`mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-[11px] font-semibold ${RESULT_CHIP[i.result]}`}>{RESULT_LABEL[i.result]}</span>
            <span>{i.criterion}{i.comment && <span className="block text-xs text-muted">“{i.comment}”</span>}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function UatStep({ d, round, can, run, onDone, onError }: { d: ChangeDetail; round: UatRoundView; can: boolean; run: (b: unknown) => Promise<ChangeDetail> } & Omit<Props, 'd'>) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(round.items.map(() => ({ result: 'accepted' as 'accepted' | 'rejected', comment: '' })));
  const [by, setBy] = useState(d.ticket.requesterName);
  const [evidence, setEvidence] = useState('');
  const m = useAction(run, onDone, onError, 'Respuesta del cliente registrada.');
  const valid = by.trim().length >= 3 && evidence.trim().length >= 5 && items.every(i => i.result === 'accepted' || i.comment.trim().length >= 5);
  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-md bg-[#FFF4E5] px-3 py-2.5 text-sm text-[#6A3A00]">
        <p className="font-semibold">Esperando las pruebas del cliente · ronda {round.round}</p>
        <p className="mt-0.5 text-xs">
          Enviada {dateTime(round.sentAt)}{round.reminderSentAt ? ` · recordatorio ${dateTime(round.reminderSentAt)}` : ''}
          {round.autoAcceptAt && ` · si no responde, se acepta sola el ${dateTime(round.autoAcceptAt)}`}
        </p>
        {round.notes && <p className="mt-1 text-xs">Nota enviada: {round.notes}</p>}
      </div>
      <RoundResult r={round} />
      {can && !open && (
        <div><button type="button" className={btnSecondary} onClick={() => setOpen(true)}>Registrar respuesta del cliente</button>
          <p className="mt-1 text-xs text-muted">Si respondió por correo o en una llamada, en lugar del portal.</p></div>
      )}
      {can && open && (
        <div className="flex flex-col gap-3 rounded-md border border-line p-3">
          {round.items.map((it, k) => (
            <fieldset key={k} className="flex flex-col gap-1.5">
              <legend className="text-sm font-medium">{k + 1}. {it.criterion}</legend>
              <div className="flex gap-4 text-sm">
                {(['accepted', 'rejected'] as const).map(r => (
                  <label key={r} className="inline-flex items-center gap-1.5">
                    <input type="radio" name={`uat-${k}`} checked={items[k]!.result === r} onChange={() => setItems(items.map((x, j) => (j === k ? { ...x, result: r } : x)))} />
                    {r === 'accepted' ? 'Aceptado' : 'Rechazado'}
                  </label>
                ))}
              </div>
              {items[k]!.result === 'rejected' && (
                <input className={field} placeholder="¿Qué falla?" aria-label={`Comentario del criterio ${k + 1}`} value={items[k]!.comment}
                  onChange={e => setItems(items.map((x, j) => (j === k ? { ...x, comment: e.target.value } : x)))} />
              )}
            </fieldset>
          ))}
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm font-medium">Quién del cliente respondió
              <input className={field} value={by} onChange={e => setBy(e.target.value)} />
            </label>
            <label className="flex flex-col gap-1 text-sm font-medium">Evidencia
              <input className={field} placeholder="Correo del 3-nov, minuta, llamada…" value={evidence} onChange={e => setEvidence(e.target.value)} />
            </label>
          </div>
          <div className="flex gap-2">
            <button type="button" className={btnPrimary} disabled={!valid || m.isPending} onClick={() => m.mutate({ items, by, evidence })}>Registrar</button>
            <button type="button" className={btnSecondary} onClick={() => setOpen(false)}>Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ProductionStep({ can, run, onDone, onError }: { can: boolean; run: (b: unknown) => Promise<ChangeDetail> } & Omit<Props, 'd'>) {
  const [date, setDate] = useState(todayKey());
  const [transports, setTransports] = useState('');
  const [notes, setNotes] = useState('');
  const m = useAction(run, onDone, onError, 'Paso a producción registrado: arrancó la garantía.');
  return (
    <div className="flex flex-col gap-3">
      <p className="rounded-md bg-accent-soft px-3 py-2 text-sm text-accent-dark">El cliente aceptó las pruebas. Registra el paso a producción para activar la garantía.</p>
      {can ? (
        <>
          <div className="grid gap-3 sm:grid-cols-[180px_minmax(0,1fr)]">
            <label className="flex flex-col gap-1 text-sm font-medium">Fecha del paso
              <input type="date" className={field} max={todayKey()} value={date} onChange={e => setDate(e.target.value)} />
            </label>
            <label className="flex flex-col gap-1 text-sm font-medium"><span>Órdenes de transporte <span className="font-normal text-muted">(separadas por coma o renglón)</span></span>
              <textarea rows={2} className={`${area} font-mono`} placeholder="DEVK900123, DEVK900124" value={transports} onChange={e => setTransports(e.target.value)} />
            </label>
          </div>
          <label className="flex flex-col gap-1 text-sm font-medium"><span>Notas <span className="font-normal text-muted">(opcional)</span></span>
            <input className={field} value={notes} onChange={e => setNotes(e.target.value)} />
          </label>
          <div><button type="button" className={btnPrimary} disabled={m.isPending || !transports.trim() || !date} onClick={() => m.mutate({ date, transports, notes })}>Registrar paso a producción</button></div>
        </>
      ) : <p className="text-sm text-muted">Lo registra el dueño del ticket o el gestor del servicio.</p>}
    </div>
  );
}

function ProductionInfo({ p }: { p: NonNullable<NonNullable<ChangeDetail['delivery']>['production']> }) {
  return (
    <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1 text-sm">
      <dt className="text-muted">Producción</dt><dd>{shortDate(`${p.date}T12:00:00`)} · {p.by.name}</dd>
      <dt className="text-muted">Transportes</dt><dd className="font-mono text-xs leading-6">{p.transports.join(', ')}</dd>
      <dt className="text-muted">Garantía</dt>
      <dd>
        <span className={`rounded px-1.5 py-0.5 text-xs font-semibold ${p.inWarranty ? 'bg-accent-soft text-accent-dark' : 'bg-[#EDEBE7] text-muted'}`}>
          {p.inWarranty ? 'Vigente' : 'Vencida'}
        </span>{' '}{p.warrantyDays} días, hasta {shortDate(`${p.warrantyUntil}T12:00:00`)}
      </dd>
      {p.notes && <><dt className="text-muted">Notas</dt><dd>{p.notes}</dd></>}
    </dl>
  );
}

function CloseStep({ d, can, run, onDone, onError }: { d: ChangeDetail; can: boolean; run: (b: unknown) => Promise<ChangeDetail> } & Omit<Props, 'd'>) {
  const [notes, setNotes] = useState('');
  const docs = d.attachments ?? [];
  const [functionalDocId, setFunctional] = useState('');
  const [technicalDocId, setTechnical] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const [target, setTarget] = useState<'functional' | 'technical'>('functional');
  const m = useAction(run, onDone, onError, 'Cambio cerrado: el ticket quedó cerrado y el cliente recibió el aviso con la garantía.');
  const abap = Boolean(d.requiresAbap);
  const ready = notes.trim().length >= 10 && functionalDocId && (!abap || technicalDocId);

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    try {
      const a = await uploadAttachment(d.ticket.number, file, false);
      if (target === 'functional') setFunctional(a.id); else setTechnical(a.id);
      // Recarga el detalle para que el archivo aparezca en la lista.
      onDone(await api<ChangeDetail>(`/change/${d.ticket.number}`), `Se adjuntó ${file.name}.`);
    } catch (e) { onError(e instanceof ApiError ? e : new Error('No se pudo subir el archivo.')); } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const docSelect = (label: string, value: string, set: (v: string) => void, which: 'functional' | 'technical') => (
    <div className="flex flex-col gap-1 text-sm font-medium">
      <label htmlFor={`doc-${which}`}>{label}</label>
      <div className="flex gap-2">
        <select id={`doc-${which}`} className={field} value={value} onChange={e => set(e.target.value)}>
          <option value="">Elige un archivo del ticket…</option>
          {docs.map(a => <option key={a.id} value={a.id}>{a.fileName}</option>)}
        </select>
        <button type="button" className={btnSecondary} disabled={uploading} onClick={() => { setTarget(which); fileRef.current?.click(); }}>Subir</button>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-3">
      <ProductionInfo p={d.delivery!.production!} />
      {can ? (
        <div className="flex flex-col gap-3 border-t border-line pt-3">
          <h3 className="text-sm font-semibold">Cierre del cambio</h3>
          <div className={`grid gap-3 ${abap ? 'sm:grid-cols-2' : ''}`}>
            {docSelect('Documentación funcional', functionalDocId, setFunctional, 'functional')}
            {abap && docSelect('Documentación técnica (ABAP)', technicalDocId, setTechnical, 'technical')}
          </div>
          <input ref={fileRef} type="file" className="hidden" aria-hidden="true" tabIndex={-1} onChange={e => void onFile(e.target.files?.[0])} />
          <label className="flex flex-col gap-1 text-sm font-medium">Notas de cierre
            <textarea rows={3} className={area} placeholder="Qué se entregó, pendientes, recomendaciones…" value={notes} onChange={e => setNotes(e.target.value)} />
          </label>
          <div className="flex flex-wrap items-center gap-3">
            <button type="button" className={btnPrimary} disabled={!ready || m.isPending} onClick={() => m.mutate({ notes, functionalDocId: functionalDocId || null, technicalDocId: technicalDocId || null })}>Cerrar el cambio</button>
            <span className="text-xs text-muted">Resuelve y cierra el ticket; el cliente recibe la fecha de garantía.</span>
          </div>
        </div>
      ) : <p className="text-sm text-muted">El cierre lo hace el dueño del ticket o el gestor del servicio.</p>}
    </div>
  );
}

function ClosedSummary({ d }: { d: ChangeDetail }) {
  const p = d.delivery?.production;
  const c = d.delivery?.closing;
  const doc = (id: string | null | undefined) => d.attachments?.find(a => a.id === id)?.fileName ?? '—';
  return (
    <div className="flex flex-col gap-3">
      <p className="rounded-md bg-accent-soft px-3 py-2 text-sm font-semibold text-accent-dark">Cambio cerrado{c?.at ? ` el ${dateTime(c.at)} por ${c.by.name}` : ''}.</p>
      {p && <ProductionInfo p={p} />}
      {c && (
        <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1 text-sm">
          <dt className="text-muted">Funcional</dt><dd>{doc(c.functionalDocId)}</dd>
          {c.technicalDocId && <><dt className="text-muted">Técnica</dt><dd>{doc(c.technicalDocId)}</dd></>}
          <dt className="text-muted">Notas</dt><dd className="whitespace-pre-line">{c.notes}</dd>
        </dl>
      )}
    </div>
  );
}
